import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, Loader2, Send, DollarSign } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteRequestModal = ({ isOpen, onClose, engagementType = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    engagement_type: engagementType,
    timeline: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEngagementChange = (value) => {
    setFormData((prev) => ({ ...prev, engagement_type: value }));
  };

  const handleTimelineChange = (value) => {
    setFormData((prev) => ({ ...prev, timeline: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      // Send as a consulting inquiry to the contact endpoint
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        inquiry_type: 'consulting',
        message: `Quote Request

Engagement Type: ${formData.engagement_type}
Timeline: ${formData.timeline}

${formData.message}`,
      });
      setStatus('success');
      // Reset form after 3 seconds and close
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          engagement_type: engagementType,
          timeline: '',
          message: '',
        });
        setStatus('idle');
        onClose();
      }, 3000);
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleClose = () => {
    if (status !== 'loading') {
      setStatus('idle');
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="quote-request-modal">
        <DialogHeader>
          <DialogTitle className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] flex items-center gap-2">
            <DollarSign className="text-[#3498DB]" size={24} />
            Request Custom Quote
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Tell me about your project and I'll provide a tailored proposal within 24-48 hours.
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle className="mx-auto text-[#27AE60] mb-4" size={56} />
            <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-2">
              Quote Request Received!
            </h3>
            <p className="text-[#64748B]">
              I'll review your request and send you a custom proposal within 24-48 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-name" className="text-[#2C3E50] font-medium">
                  Name *
                </Label>
                <Input
                  id="quote-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="border-slate-200 focus:border-[#3498DB]"
                  data-testid="quote-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-email" className="text-[#2C3E50] font-medium">
                  Email *
                </Label>
                <Input
                  id="quote-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="border-slate-200 focus:border-[#3498DB]"
                  data-testid="quote-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote-company" className="text-[#2C3E50] font-medium">
                Company *
              </Label>
              <Input
                id="quote-company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company name"
                required
                className="border-slate-200 focus:border-[#3498DB]"
                data-testid="quote-company-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-engagement" className="text-[#2C3E50] font-medium">
                  Engagement Type *
                </Label>
                <Select value={formData.engagement_type} onValueChange={handleEngagementChange} required>
                  <SelectTrigger className="border-slate-200 focus:border-[#3498DB]" data-testid="quote-engagement-select">
                    <SelectValue placeholder="Select engagement type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Strategic Advisory">Strategic Advisory</SelectItem>
                    <SelectItem value="Implementation Partnership">Implementation Partnership</SelectItem>
                    <SelectItem value="Ongoing Advisory">Ongoing Advisory</SelectItem>
                    <SelectItem value="Not Sure">Not Sure - Need Guidance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-timeline" className="text-[#2C3E50] font-medium">
                  Preferred Timeline *
                </Label>
                <Select value={formData.timeline} onValueChange={handleTimelineChange} required>
                  <SelectTrigger className="border-slate-200 focus:border-[#3498DB]" data-testid="quote-timeline-select">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASAP (1-2 weeks)">ASAP (1-2 weeks)</SelectItem>
                    <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                    <SelectItem value="1-3 months">1-3 months</SelectItem>
                    <SelectItem value="3-6 months">3-6 months</SelectItem>
                    <SelectItem value="Flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote-message" className="text-[#2C3E50] font-medium">
                Project Details *
              </Label>
              <Textarea
                id="quote-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Briefly describe your project, current challenges, and what you're looking to achieve..."
                required
                rows={4}
                className="border-slate-200 focus:border-[#3498DB] resize-none"
                data-testid="quote-message-input"
              />
            </div>

            {status === 'error' && (
              <p className="text-red-500 text-sm" data-testid="quote-error">{errorMessage}</p>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={status === 'loading'}
                className="flex-1 border-slate-200"
                data-testid="quote-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={status === 'loading'}
                className="flex-1 bg-[#3498DB] hover:bg-[#2980B9] text-white shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
                data-testid="quote-submit"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Request Quote
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
